import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Typography,
  Divider,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Close, Add, Delete } from "@mui/icons-material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { selectStatusOptions } from "../../redux/slices/statusDefinitionsSlice";

const blankLine = () => ({ description: "", quantity: 1, unitPrice: "" });

// Editing an invoice is allowed at any status — including after it's been
// sent to the customer — since admins routinely need to correct a mistake
// or add a late charge. The server enforces no status gate either.
const EditInvoiceDialog = ({ open, onClose, onSubmit, loading, invoice }) => {
  const statusOptions = useSelector(selectStatusOptions("invoice"));
  const [lines, setLines] = useState([blankLine()]);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (open && invoice) {
      setLines(
        invoice.lines?.length
          ? invoice.lines.map((l) => ({ description: l.description, quantity: l.quantity || 1, unitPrice: l.unitPrice }))
          : [blankLine()],
      );
      setDueDate(invoice.dueDate ? dayjs(invoice.dueDate).format("YYYY-MM-DD") : "");
      setNotes(invoice.notes || "");
      setStatus(invoice.status || "");
    }
  }, [open, invoice]);

  const updateLine = (idx, field) => (e) => {
    const value = e.target.value;
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const total = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 1) * (parseFloat(l.unitPrice) || 0), 0);

  const canSubmit = lines.some((l) => l.description && parseFloat(l.unitPrice) > 0) && !!dueDate && !loading;

  const handleSubmit = () => {
    onSubmit({
      lines: lines
        .filter((l) => l.description && parseFloat(l.unitPrice) >= 0)
        .map((l) => ({
          description: l.description,
          quantity: parseFloat(l.quantity) || 1,
          unitPrice: parseFloat(l.unitPrice) || 0,
        })),
      dueDate,
      notes,
      status,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography fontWeight={700}>Edit Invoice {invoice?.invoiceNumber}</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Due Date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              required
            />
            <TextField label="Status" select value={status} onChange={(e) => setStatus(e.target.value)}>
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Divider>
            <Typography variant="caption" color="text.secondary">
              Line Items
            </Typography>
          </Divider>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {lines.map((line, idx) => (
              <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  label="Description"
                  value={line.description}
                  onChange={updateLine(idx, "description")}
                  sx={{ flex: 2 }}
                />
                <TextField
                  size="small"
                  label="Qty"
                  type="number"
                  value={line.quantity}
                  onChange={updateLine(idx, "quantity")}
                  sx={{ width: 70 }}
                />
                <TextField
                  size="small"
                  label="Unit Price"
                  type="number"
                  value={line.unitPrice}
                  onChange={updateLine(idx, "unitPrice")}
                  sx={{ width: 110 }}
                  slotProps={{
                    input: {
                      startAdornment: <Typography sx={{ mr: 0.5, color: "text.secondary", fontSize: 13 }}>$</Typography>,
                    },
                  }}
                />
                <IconButton size="small" onClick={() => removeLine(idx)} disabled={lines.length === 1}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<Add />} onClick={addLine} sx={{ alignSelf: "flex-start" }}>
              Add Line
            </Button>
          </Box>

          <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 2, display: "flex", justifyContent: "space-between" }}>
            <Typography fontWeight={700}>Total Invoice Amount</Typography>
            <Typography fontWeight={700} fontSize={17} color="primary.main">
              ${total.toFixed(2)}
            </Typography>
          </Box>

          <TextField label="Notes (optional)" multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {loading ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvoiceDialog;
